import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { render } from '@/threejs/core';
import { FatLinesBatch } from '@/threejs/core/FatLinesBatch';
import { useEventListener } from '@vueuse/core';
import * as THREE from 'three';

/**
 * EdgesManager - Centralized management for object edge rendering
 * Handles edge visibility, color, thickness, and global double-sided material settings
 * Integrates FatLinesBatch (FLB) system for efficient edge rendering
 */
class EdgesManager {
  constructor() {
    this.showObjectEdges = false;
    this.edgeColor = '#ffffff';
    this.edgeThickness = 1.0;
    this.globalDoubleSided = false;
    this.isInitialized = false;

    // FatLinesBatch (FLB) properties
    this.scene = null;
    this.flb = null;
    this.flbMeshMapping = [];

    // Lazy initialization of store to avoid calling before Pinia is ready
    this._cruxModuleStore = null;
  }

  /**
   * Initialize the edges manager with scene reference
   * @param {THREE.Scene} scene - The Three.js scene to manage edges for
   */
  initialize() {
    this.isInitialized = true;
    return this;
  }

  initializeEdges(scene) {
    if (!scene) {
      throw new Error('EdgesManager requires a scene to initialize');
    }

    this.scene = scene;

    // Set up event listeners for object changes using VueUse
    useEventListener(window, 'objectAdded', (event) => {
      this.refreshFlb();
    });

    useEventListener(window, 'objectUpdated', (event) => {
      this.refreshFlb();
    });

    useEventListener(window, 'objectRemoved', (event) => {
      this.refreshFlb();
    });
  }
  /**
   * Check if manager is initialized
   * @returns {boolean}
   */
  isManagerInitialized() {
    return this.isInitialized;
  }

  /**
   * Get crux module store (lazy initialization)
   * @returns {object} Crux module store
   */
  getCruxModuleStore() {
    if (!this._cruxModuleStore) {
      this._cruxModuleStore = useCruxModuleStore();
    }
    return this._cruxModuleStore;
  }

  /**
   * Get view setting value
   * @param {string} key - Setting key
   * @param {*} defaultValue - Default value if setting not found
   * @returns {*} Setting value
   */
  getViewSetting(key, defaultValue = null) {
    return this.getCruxModuleStore().getSettingValue('viewport_settings', key, defaultValue);
  }

  /**
   * Set object edges visibility
   * @param {boolean} visible - Whether object edges should be visible
   */
  setObjectEdgesVisible(visible) {
    this.showObjectEdges = visible;
    this.refreshFlb();
    render();
  }

  /**
   * Set object edge color
   * @param {string|number} color - Color for object edges
   */
  setObjectEdgeColor(color) {
    color = color.startsWith('#') ? color : `#${color}`;
    this.edgeColor = color;
    this.setFlbEdgeColor(color);
    render();
  }

  /**
   * Set object edge thickness
   * @param {number} thickness - Thickness for object edges
   */
  setObjectEdgeThickness(thickness) {
    this.edgeThickness = thickness;
    this.setFlbEdgeThickness(thickness);
    render();
  }

  /**
   * Turn on FatLinesBatch edges
   */
  turnOnFlb() {
    if (!this.scene) {
      console.warn('EdgesManager: Scene not initialized');
      return;
    }

    this.turnOffFlb();

    let gs = [];
    this.flbMeshMapping = []; // Reset mesh mapping

    this.scene.traverse((child) => {
      child.updateMatrixWorld();
      // Skip internal Three.js objects (lights, cameras, etc.)
      if (child.isLight || child.isCamera) return;
      // Skip explicitly excluded objects
      if (child.userData.excludeFromOutline) return;

      if (child.visible === false) return;

      if (child.userData.edgeOutline) {
        if (child.isMesh) {
          gs.push(child.geometry.clone().applyMatrix4(child.matrixWorld));
          this.flbMeshMapping.push(child); // Store reference to the mesh
        }
      }
    });

    if (gs.length > 0) {
      const edgeColor = this.getViewSetting('render.objectEdgeColour', '#ffffff');
      const edgeThickness = this.getViewSetting('render.objectEdgeThickness', 2);

      this.flb = new FatLinesBatch(gs, {
        color: edgeColor,
        thickness: edgeThickness,
      });

      // Check if FatLinesBatch was successfully created with valid edges
      if (this.flb.items && this.flb.items.length > 0 && this.flb.colorIdx && this.flb.colorIdx.length > 0) {
        this.flb.items.forEach((it, idx) => {
          let color = edgeColor.startsWith('#') ? edgeColor : `#${edgeColor}`;
          this.flb.setColorAt(idx, color);
        });
        this.flb.update();
        this.scene.add(this.flb);
      } else {
        console.warn('No valid edges to display for outlined objects');
        this.flb = null;
      }
    }
  }

  /**
   * Turn off FatLinesBatch edges
   */
  turnOffFlb() {
    if (this.flb && this.scene) {
      this.scene.remove(this.flb);
      this.flb = null;
    }
    this.flbMeshMapping = [];
  }

  /**
   * Refresh FatLinesBatch edges based on current settings
   */
  refreshFlb() {
    if (!this.scene) {
      console.warn('EdgesManager: Scene not initialized');
      return;
    }

    if (this.getViewSetting('render.showObjectEdges', false)) {
      this.turnOnFlb();
    } else {
      this.turnOffFlb();
    }
    render();
  }

  /**
   * Set FLB edge color
   * @param {string|number} color - Color for FLB edges
   */
  setFlbEdgeColor(color) {
    if (this.flb) {
      color = color.startsWith('#') ? color : `#${color}`;
      this.flb.setEdgeColor(color);
    }
    render();
  }

  /**
   * Set FLB edge thickness
   * @param {number} thickness - Thickness for FLB edges
   */
  setFlbEdgeThickness(thickness) {
    if (this.flb) {
      this.flb.setEdgeThickness(thickness);
    }
    render();
  }

  /**
   * Set global double-sided material mode
   * @param {boolean} doubleSided - Whether all materials should be double-sided
   */
  setGlobalDoubleSided(doubleSided) {
    this.globalDoubleSided = doubleSided;
    if (this.scene) {
      this.scene.traverse((child) => {
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach((material) => {
              material.side = doubleSided ? THREE.DoubleSide : THREE.FrontSide;
              material.needsUpdate = true;
            });
          } else {
            child.material.side = doubleSided ? THREE.DoubleSide : THREE.FrontSide;
            child.material.needsUpdate = true;
          }
        }
      });
    }

    render();
  }

  /**
   * Apply edge settings to a specific object
   * @param {THREE.Object3D} object - Object to apply edge settings to
   */
  applyEdgeSettingsToObject(object) {
    if (!object || !object.material) {
      return;
    }

    // Apply double-sided setting
    if (Array.isArray(object.material)) {
      object.material.forEach((material) => {
        material.side = this.globalDoubleSided ? THREE.DoubleSide : THREE.FrontSide;
        material.needsUpdate = true;
      });
    } else {
      object.material.side = this.globalDoubleSided ? THREE.DoubleSide : THREE.FrontSide;
      object.material.needsUpdate = true;
    }

    // If edges are enabled, this would trigger the edge geometry update
    if (this.showObjectEdges) {
      // This would integrate with your edge geometry system
      this.updateObjectEdges(object);
    }
  }

  /**
   * Update edge geometry for a specific object
   * @param {THREE.Object3D} object - Object to update edges for
   */
  updateObjectEdges(object) {
    // This is where you would implement object-specific edge updates
    // The actual implementation depends on your edge rendering system

    // Example implementation - adjust according to your system:
    if (object.geometry && this.showObjectEdges) {
      // Remove existing edge geometry if it exists
      const existingEdges = object.getObjectByName('edges');
      if (existingEdges) {
        object.remove(existingEdges);
        existingEdges.geometry?.dispose();
        existingEdges.material?.dispose();
      }

      // Create new edge geometry
      const edges = new THREE.EdgesGeometry(object.geometry);
      const lineMaterial = new THREE.LineBasicMaterial({
        color: this.edgeColor,
        linewidth: this.edgeThickness,
      });
      const edgeLines = new THREE.LineSegments(edges, lineMaterial);
      edgeLines.name = 'edges';
      object.add(edgeLines);
    }
  }

  /**
   * Remove edges from all objects
   */
  removeAllEdges() {
    if (this.scene) {
      this.scene.traverse((child) => {
        const edges = child.getObjectByName('edges');
        if (edges) {
          child.remove(edges);
          edges.geometry?.dispose();
          edges.material?.dispose();
        }
      });
    }
  }

  /**
   * Handle edges setting changes
   * @param {string} key - Setting key
   * @param {*} value - Setting value
   */
  handleEdgesSetting(key, value) {
    switch (key) {
      case 'render.showObjectEdges':
        this.setObjectEdgesVisible(value);
        break;
      case 'render.objectEdgeColour':
        this.setObjectEdgeColor(value);
        break;
      case 'render.objectEdgeThickness':
        this.setObjectEdgeThickness(value);
        break;
      case 'render.globalDoubleSided':
        this.setGlobalDoubleSided(value);
        break;
      default:
        console.warn(`Unknown edges setting: ${key}`);
    }
  }

  /**
   * Get current edges configuration
   * @returns {object} Current edges settings
   */
  getEdgesConfig() {
    return {
      showObjectEdges: this.showObjectEdges,
      edgeColor: this.edgeColor,
      edgeThickness: this.edgeThickness,
      globalDoubleSided: this.globalDoubleSided,
    };
  }

  /**
   * Dispose of edges resources
   */
  dispose() {
    this.removeAllEdges();
    this.turnOffFlb();
    this.scene = null;
    this._cruxModuleStore = null;
    this.isInitialized = false;
  }
}

// Create and export singleton instance
export const edgesManager = new EdgesManager();

// Export the class for testing or multiple instances if needed
export { EdgesManager };
